import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import {
  Phone, MapPin, Clock, Mail, Send, Menu, X, Quote, ArrowRight, CheckCircle2, Siren, ShieldCheck, HeartHandshake, Sparkles, Lock,
  ScanEye, Microscope, Scissors, Expand, Droplet, Activity, Stethoscope, ShieldPlus, Target,
} from "lucide-react";
import { SITE, WHATSAPP_URL, IMG, FEATURED, SERVICES, WARNING_SIGNS, TESTIMONIALS, TOPICS, CPRE_GALLERY } from "@/lib/site";

const Icons = { ScanEye, Microscope, Scissors, Expand, Droplet, Activity, Stethoscope, ShieldPlus, Target };
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fade = (d = 0) => ({ initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, delay: d } });

const WA = ({ className = "", children = "Agendar por WhatsApp" }) => (
  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"
    className={`inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1EBE5A] text-white font-semibold px-6 py-3.5 shadow-lg shadow-[#25D366]/25 transition ${className}`}>
    <WaIcon /> {children}
  </a>
);

function WaIcon({ size = 18 }) {
  return <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true"><path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.88-.79-1.48-1.76-1.65-2.06-.17-.3-.02-.46.13-.6.13-.14.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.21-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.18-1.41-.08-.13-.27-.2-.57-.35M12.05 21.8a9.87 9.87 0 01-5.03-1.38l-.36-.21-3.74.98 1-3.65-.24-.37a9.86 9.86 0 01-1.51-5.26c0-5.45 4.44-9.88 9.9-9.88a9.86 9.86 0 019.88 9.89c0 5.45-4.44 9.88-9.89 9.88M20.46 3.49A11.82 11.82 0 0012.05 0C5.5 0 .16 5.34.16 11.89c0 2.1.55 4.14 1.59 5.95L.06 24l6.3-1.65a11.88 11.88 0 005.69 1.45c6.55 0 11.89-5.34 11.89-11.89 0-3.18-1.24-6.17-3.49-8.42" /></svg>;
}

function Nav() {
  const [open, setOpen] = useState(false);
  const links = [["#servicios", "Servicios"], ["#cpre", "CPRE"], ["#temas", "Temas de salud"], ["#sobre-mi", "Sobre mí"], ["#testimonios", "Testimonios"], ["#contacto", "Contacto"]];
  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/85 backdrop-blur-lg border-b border-slate-200/70">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between gap-4">
        <a href="#inicio" aria-label="Inicio"><img src={IMG.logo} alt="Dr. Pablo Salgado · Gastroenterología" className="h-[60px] w-auto" /></a>
        <nav className="hidden lg:flex items-center gap-7 text-[15px] font-medium text-slate-600">
          {links.map(([h, l]) => <a key={h} href={h} className="hover:text-[#1D4ED8] transition">{l}</a>)}
        </nav>
        <div className="hidden lg:flex items-center gap-4">
          <Link to="/login" className="text-sm text-slate-500 hover:text-[#1D4ED8] inline-flex items-center gap-1.5"><Lock size={14} /> Acceso clínico</Link>
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="rounded-full bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white text-sm font-semibold px-5 py-2.5 transition">Agendar cita</a>
        </div>
        <button className="lg:hidden p-2 text-slate-700" onClick={() => setOpen((o) => !o)} aria-label="Menú">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200 px-5 py-4 space-y-1">
          {links.map(([h, l]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 text-slate-700 font-medium">{l}</a>)}
          <Link to="/login" className="block py-2.5 text-slate-500">Acceso clínico</Link>
          <WA className="w-full mt-2" />
        </div>
      )}
    </header>
  );
}

function Contact() {
  const empty = { nombre: "", telefono: "", email: "", motivo: "", mensaje: "" };
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.telefono.trim()) { toast.error("Escribe tu nombre y teléfono"); return; }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      toast.success(data.message || "¡Gracias! Te contactaremos pronto.");
      setForm(empty);
    } catch (err) {
      toast.error("No se pudo enviar. Escríbenos por WhatsApp.");
    } finally { setSending(false); }
  };
  const inp = "w-full rounded-xl bg-white/10 border border-white/15 px-4 py-3 text-white placeholder:text-white/45 outline-none focus:border-sky-300 focus:bg-white/15 transition";
  return (
    <form onSubmit={submit} className="rounded-3xl bg-white/[0.06] border border-white/10 p-6 sm:p-8 space-y-3" aria-label="Solicitar cita">
      <h3 className="font-head text-xl font-bold text-white mb-1">Solicita tu cita</h3>
      <p className="text-sm text-white/60 mb-4">Te respondemos en horario de consulta.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <input className={inp} placeholder="Nombre completo *" value={form.nombre} onChange={set("nombre")} autoComplete="name" />
        <input className={inp} placeholder="Teléfono / WhatsApp *" value={form.telefono} onChange={set("telefono")} inputMode="tel" autoComplete="tel" />
      </div>
      <input className={inp} type="email" placeholder="Correo (opcional)" value={form.email} onChange={set("email")} autoComplete="email" />
      <select className={`${inp} [&>option]:text-slate-900`} value={form.motivo} onChange={set("motivo")}>
        <option value="">Motivo de consulta</option>
        {["Consulta de gastroenterología", "Endoscopía", "Colonoscopía", "CPRE", "Tratamiento de obesidad", "Hígado graso / hepatitis", "Otro"].map((m) => <option key={m}>{m}</option>)}
      </select>
      <textarea className={`${inp} resize-none`} rows={3} placeholder="Cuéntanos brevemente tus síntomas (opcional)" value={form.mensaje} onChange={set("mensaje")} />
      <button type="submit" disabled={sending} className="w-full rounded-xl bg-gradient-to-r from-[#1D4ED8] to-[#0EA5E9] text-white font-semibold py-3.5 inline-flex items-center justify-center gap-2 hover:brightness-110 disabled:opacity-60 transition">
        <Send size={17} /> {sending ? "Enviando…" : "Enviar solicitud"}
      </button>
      <p className="text-[11px] text-white/40 text-center">Para emergencias llama directamente al {SITE.emergency}.</p>
    </form>
  );
}

export default function Landing() {
  return (
    <div className="bg-white text-slate-800 overflow-x-hidden">
      <Nav />

      {/* HERO */}
      <section id="inicio" className="relative pt-[76px] bg-gradient-to-b from-[#EEF4FF] via-white to-white">
        <div className="absolute -top-40 -right-40 w-[620px] h-[620px] rounded-full bg-[#1D4ED8]/10 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.05fr_1fr] gap-10 items-center py-14 lg:py-20">
          <motion.div {...fade()}>
            <span className="inline-flex items-center gap-2 rounded-full bg-[#1D4ED8]/10 text-[#1D4ED8] text-xs font-semibold tracking-wide uppercase px-3.5 py-1.5">
              <Sparkles size={14} /> {SITE.specialty}
            </span>
            <h1 className="font-head mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-[#0F1E4D]">
              Cuidamos tu salud digestiva con <span className="bg-gradient-to-r from-[#1D4ED8] to-[#0EA5E9] bg-clip-text text-transparent">experiencia y empatía</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Diagnóstico y tratamiento de enfermedades del esófago, estómago, intestino, hígado y páncreas, con endoscopía digestiva avanzada y atención cercana.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <WA />
              <a href="#servicios" className="inline-flex items-center gap-2 rounded-full border border-slate-300 hover:border-[#1D4ED8] text-slate-700 hover:text-[#1D4ED8] font-semibold px-6 py-3.5 transition">Ver servicios <ArrowRight size={17} /></a>
            </div>
            <dl className="mt-10 grid grid-cols-3 max-w-md gap-4">
              {[["300+", "Pacientes atendidos"], ["300+", "Procedimientos"], ["24/7", "Emergencias"]].map(([n, l]) => (
                <div key={l}><dt className="sr-only">{l}</dt><dd className="font-head text-3xl font-extrabold text-[#0F1E4D]">{n}</dd><p className="text-xs text-slate-500 mt-0.5">{l}</p></div>
              ))}
            </dl>
          </motion.div>
          <motion.div {...fade(0.15)} className="relative mx-auto w-full max-w-[520px]">
            <div className="absolute inset-x-6 bottom-0 top-16 rounded-[3rem] bg-gradient-to-br from-[#1E3A8A] via-[#1D4ED8] to-[#0EA5E9]" aria-hidden="true" />
            <div className="absolute inset-x-6 bottom-0 top-16 rounded-[3rem] opacity-30" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,.35) 1px, transparent 1px)", backgroundSize: "18px 18px" }} aria-hidden="true" />
            <img src={IMG.doctor} alt={`${SITE.fullName}, gastroenterólogo`} className="relative w-full h-auto" />
            <div className="absolute left-0 bottom-10 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center"><Siren size={20} /></span>
              <div><p className="text-xs text-slate-500">Emergencias 24/7</p><a href={`tel:${SITE.emergency.replace(/\s/g, "")}`} className="font-bold text-[#0F1E4D]">{SITE.emergency}</a></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROCEDIMIENTOS DESTACADOS */}
      <section id="procedimientos" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fade()} className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1D4ED8]">Gastroenterología y endoscopía</p>
            <h2 className="font-head mt-3 text-3xl sm:text-4xl font-extrabold text-[#0F1E4D]">Procedimientos con tecnología y precisión</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {FEATURED.map((f, i) => (
              <motion.article key={f.id} {...fade(i * 0.1)} className="group rounded-3xl overflow-hidden bg-white border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition duration-300">
                <div className="aspect-[4/3] overflow-hidden bg-slate-100"><img src={IMG[f.img]} alt={f.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" /></div>
                <div className="p-6">
                  <h3 className="font-head text-xl font-bold text-[#0F1E4D]">{f.title}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed text-[15px]">{f.text}</p>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICIOS */}
      <section id="servicios" className="py-20 lg:py-28 bg-[#F4F7FD]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fade()} className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div className="max-w-2xl">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1D4ED8]">Servicios</p>
              <h2 className="font-head mt-3 text-3xl sm:text-4xl font-extrabold text-[#0F1E4D]">Atención integral del aparato digestivo</h2>
            </div>
            <WA className="self-start md:self-auto">Consultar por WhatsApp</WA>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Ic = Icons[s.icon] || Icons.Stethoscope;
              return (
                <motion.div key={s.title} {...fade((i % 3) * 0.08)} className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-[#1D4ED8]/40 hover:shadow-lg transition">
                  <span className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1D4ED8] to-[#0EA5E9] text-white flex items-center justify-center"><Ic size={22} /></span>
                  <h3 className="font-head mt-4 text-lg font-bold text-[#0F1E4D]">{s.title}</h3>
                  <p className="mt-1.5 text-slate-600 text-[15px] leading-relaxed">{s.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CPRE / ENDOSCOPIA AVANZADA */}
      <section id="cpre" className="py-20 lg:py-28 bg-[#0B1533] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-12 items-center">
          <motion.div {...fade()}>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-sky-300">Endoscopía avanzada</p>
            <h2 className="font-head mt-3 text-3xl sm:text-4xl font-extrabold">CPRE: cálculos biliares sin cirugía abierta</h2>
            <p className="mt-5 text-white/75 leading-relaxed">Cuando los cálculos pasan de la vesícula al conducto biliar (colédoco) pueden causar dolor intenso, piel amarilla (ictericia), fiebre o pancreatitis. Con la CPRE accedemos a la vía biliar a través de la boca, sin incisiones, para:</p>
            <ul className="mt-5 space-y-3">
              {["Extraer uno o múltiples cálculos del colédoco", "Colocar stents biliares cuando hay estrechez o tumores", "Restablecer el drenaje de la bilis con recuperación más rápida"].map((x) => (
                <li key={x} className="flex gap-3 text-white/85"><CheckCircle2 size={20} className="text-sky-300 shrink-0 mt-0.5" /> {x}</li>
              ))}
            </ul>
            <p className="mt-5 text-sm text-white/55">Procedimientos realizados en el {SITE.hospital}.</p>
            <WA className="mt-8">Consultar por CPRE</WA>
          </motion.div>
          <motion.div {...fade(0.1)} className="grid grid-cols-2 gap-4">
            <div className="col-span-2 rounded-3xl overflow-hidden border border-white/10 aspect-[16/10]"><img src={CPRE_GALLERY[0].img} alt={CPRE_GALLERY[0].alt} loading="lazy" className="w-full h-full object-cover object-top" /></div>
            {CPRE_GALLERY.slice(1).map((g) => (
              <div key={g.img} className="rounded-3xl overflow-hidden border border-white/10 aspect-[9/14]"><img src={g.img} alt={g.alt} loading="lazy" className="w-full h-full object-cover" /></div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* VIDEO */}
      <section id="video" className="py-20 lg:py-28 bg-gradient-to-b from-[#EEF4FF] to-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-[minmax(0,340px)_1fr] gap-10 lg:gap-16 items-center">
          <motion.div {...fade()} className="mx-auto w-full max-w-[340px]">
            <div className="rounded-[2rem] overflow-hidden bg-black shadow-2xl shadow-[#1E3A8A]/25 border-4 border-white aspect-[9/16]">
              <video src="/video/video-dr-salgado.mp4" poster="/video/video-poster.webp" controls playsInline preload="none" className="w-full h-full object-cover">
                Tu navegador no puede reproducir este video.
              </video>
            </div>
          </motion.div>
          <motion.div {...fade(0.1)}>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1D4ED8]">Video</p>
            <h2 className="font-head mt-3 text-3xl sm:text-4xl font-extrabold text-[#0F1E4D]">¿Ardor o dolor en el estómago? Podría ser una úlcera péptica</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">El {SITE.doctor} explica los síntomas de la úlcera péptica y cómo la endoscopía permite diagnosticarla y tratarla a tiempo.</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {[["Dolor abdominal", "Ardor en la parte alta del abdomen, sobre todo con el estómago vacío."], ["Acidez y náuseas", "Sensación de acidez, llenura, eructos y a veces vómito."], ["Sangrado digestivo", "Vómito con sangre o heces oscuras (melena): es una emergencia."], ["Perforación", "Dolor súbito e intenso; requiere atención inmediata."]].map(([t, d], i) => (
                <div key={t} className="rounded-2xl bg-white border border-slate-200 p-4 flex gap-3">
                  <span className="w-8 h-8 shrink-0 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#0EA5E9] text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                  <div><p className="font-semibold text-[#0F1E4D]">{t}</p><p className="text-sm text-slate-600 mt-0.5">{d}</p></div>
                </div>
              ))}
            </div>
            <WA className="mt-8">Agendar una endoscopía</WA>
          </motion.div>
        </div>
      </section>

      {/* TEMAS DE SALUD */}
      <section id="temas" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fade()} className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1D4ED8]">Temas de salud</p>
            <h2 className="font-head mt-3 text-3xl sm:text-4xl font-extrabold text-[#0F1E4D]">Lo que más nos consultan</h2>
            <p className="mt-3 text-slate-600">Información breve para que sepas cuándo consultar. Cada caso se valora de forma personal.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TOPICS.map((t, i) => (
              <motion.article key={t.title} {...fade((i % 3) * 0.08)} className="group flex flex-col rounded-3xl overflow-hidden bg-white border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition duration-300">
                <div className="aspect-square overflow-hidden bg-slate-100"><img src={t.img} alt={t.alt} loading="lazy" className={`w-full h-full object-cover ${t.pos || ""} group-hover:scale-[1.03] transition duration-500`} /></div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-head text-lg font-bold text-[#0F1E4D] leading-snug">{t.title}</h3>
                  <p className="mt-2 text-slate-600 text-sm leading-relaxed flex-1">{t.text}</p>
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1D4ED8] hover:gap-2.5 transition-all">Consultar <ArrowRight size={15} /></a>
                </div>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* CUANDO CONSULTAR */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade()} className="relative order-2 lg:order-1 flex justify-center">
            <div className="absolute w-[80%] aspect-square rounded-full bg-[#EEF4FF]" aria-hidden="true" />
            <img src={IMG.anatomia} alt="Ilustración del aparato digestivo" loading="lazy" className="relative max-h-[520px] w-auto" />
          </motion.div>
          <motion.div {...fade(0.1)} className="order-1 lg:order-2">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1D4ED8]">Prevención</p>
            <h2 className="font-head mt-3 text-3xl sm:text-4xl font-extrabold text-[#0F1E4D]">¿Cuándo consultar a un gastroenterólogo?</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">Muchas enfermedades digestivas se tratan mejor cuando se detectan a tiempo. Agenda una valoración si tienes:</p>
            <ul className="mt-6 grid sm:grid-cols-2 gap-3">
              {WARNING_SIGNS.map((w) => (
                <li key={w} className="flex gap-2.5 text-slate-700 text-[15px]"><CheckCircle2 size={19} className="text-[#0EA5E9] shrink-0 mt-0.5" /> {w}</li>
              ))}
            </ul>
            <div className="mt-7 rounded-2xl bg-[#0F1E4D] text-white p-5 flex gap-4 items-start">
              <ShieldCheck size={26} className="text-sky-300 shrink-0" />
              <p className="text-[15px] text-white/85">La colonoscopía de control se recomienda desde los <b className="text-white">45 años</b>, o antes si hay antecedentes familiares de cáncer de colon. Detectar y retirar un pólipo a tiempo previene el cáncer colorrectal.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SOBRE MI */}
      <section id="sobre-mi" className="py-20 lg:py-28 bg-gradient-to-br from-[#0F1E4D] via-[#1E3A8A] to-[#1D4ED8] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...fade()} className="relative">
            <div className="rounded-3xl overflow-hidden border border-white/15 shadow-2xl max-w-md mx-auto lg:mx-0 aspect-[4/5]"><img src={IMG.retrato} alt={SITE.fullName} loading="lazy" className="w-full h-full object-cover object-top" /></div>
            <div className="hidden sm:block absolute -bottom-8 right-0 lg:right-6 w-56 rounded-2xl overflow-hidden border-4 border-[#1E3A8A] shadow-xl"><img src={IMG.endo1} alt="El Dr. Salgado realizando una endoscopía" loading="lazy" className="w-full" /></div>
          </motion.div>
          <motion.div {...fade(0.1)}>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-sky-300">Sobre mí</p>
            <h2 className="font-head mt-3 text-3xl sm:text-4xl font-extrabold">{SITE.fullName}</h2>
            <p className="mt-2 text-sky-200 font-medium">{SITE.specialty}</p>
            <p className="mt-6 text-white/80 leading-relaxed">
              Mi compromiso es que cada paciente entienda lo que le pasa y reciba el tratamiento adecuado. Combino la endoscopía diagnóstica y terapéutica con un acompañamiento cercano, desde la primera consulta hasta el control.
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {[[HeartHandshake, "Trato humano", "Explicaciones claras y paciencia en cada consulta"], [ShieldCheck, "Seguridad", "Procedimientos con sedación y protocolos de bioseguridad"], [Siren, "Emergencias 24/7", "Atención de sangrados y urgencias digestivas"]].map(([Ic, t, d]) => (
                <div key={t} className="rounded-2xl bg-white/10 border border-white/10 p-4">
                  <Ic size={22} className="text-sky-300" />
                  <p className="font-semibold mt-2">{t}</p>
                  <p className="text-sm text-white/65 mt-1">{d}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* TESTIMONIOS */}
      <section id="testimonios" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <motion.div {...fade()} className="max-w-2xl mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#1D4ED8]">Testimonios</p>
            <h2 className="font-head mt-3 text-3xl sm:text-4xl font-extrabold text-[#0F1E4D]">¿Qué dicen los pacientes?</h2>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.figure key={t.name} {...fade(i * 0.1)} className="h-full flex flex-col rounded-3xl bg-[#F4F7FD] border border-slate-200 p-7">
                <Quote size={28} className="text-[#1D4ED8]" />
                <blockquote className="mt-4 text-slate-700 leading-relaxed flex-1">“{t.text}”</blockquote>
                <figcaption className="flex items-center gap-3 pt-5 mt-6 border-t border-slate-200">
                  <span className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1D4ED8] to-[#0EA5E9] text-white flex items-center justify-center font-bold text-sm">{t.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
                  <span className="font-head font-semibold text-[#0F1E4D]">{t.name}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACTO */}
      <footer id="contacto" className="bg-[#0B1533] text-white pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <img src={IMG.logoWhite} alt="Dr. Pablo Salgado · Gastroenterología" className="h-20 w-auto" />
            <h2 className="font-head mt-8 text-3xl font-extrabold">Agenda tu consulta</h2>
            <ul className="mt-6 space-y-4 text-white/80">
              <li className="flex gap-3"><MapPin size={20} className="text-sky-300 shrink-0" /><span><b className="text-white">{SITE.hospital}</b><br />{SITE.address}</span></li>
              <li className="flex gap-3"><Phone size={20} className="text-sky-300 shrink-0" /><span>Citas: <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} className="text-white hover:underline">{SITE.phone}</a><br />Emergencias 24/7: <a href={`tel:${SITE.emergency.replace(/\s/g, "")}`} className="text-white hover:underline">{SITE.emergency}</a></span></li>
              <li className="flex gap-3"><Mail size={20} className="text-sky-300 shrink-0" /><a href={`mailto:${SITE.email}`} className="hover:underline">{SITE.email}</a></li>
              <li className="flex gap-3"><Clock size={20} className="text-sky-300 shrink-0" /><span>{SITE.hours.map((h) => `${h.d}: ${h.h}`).join(" · ")}</span></li>
            </ul>
            <WA className="mt-8" />
          </div>
          <Contact />
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-16 pt-6 border-t border-white/10 flex flex-col sm:flex-row gap-3 justify-between text-xs text-white/45">
          <span>© {new Date().getFullYear()} {SITE.fullName} · {SITE.specialty}</span>
          <Link to="/login" className="hover:text-white">Acceso clínico</Link>
        </div>
      </footer>

      <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="Escribir por WhatsApp"
        className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-xl shadow-[#25D366]/30 hover:scale-105 transition"><WaIcon size={26} /></a>
    </div>
  );
}
